import { useEffect, useState } from "react";
import { Download, Share, X, Plus } from "lucide-react";

const DISMISS_KEY = "synergy_pwa_dismissed_at";
const DISMISS_DAYS = 7;

function isIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  // Detect iPhone / iPad — including iPad on iPadOS reporting as Mac
  const iOSDevice = /iPhone|iPad|iPod/.test(ua);
  const iPadOS = ua.includes("Mac") && "ontouchend" in document;
  return iOSDevice || iPadOS;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function recentlyDismissed() {
  const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
  if (!ts) return false;
  return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

export default function InstallPrompt() {
  const [deferredEvent, setDeferredEvent] = useState(null); // Chrome / Android
  const [showIOSSheet, setShowIOSSheet] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    // Chrome / Edge / Android — capture the native install prompt.
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredEvent(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari — no native event. Show our own sheet after 12s of use.
    let timer = null;
    if (isIOS()) {
      timer = setTimeout(() => setShowIOSSheet(true), 12_000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      if (timer) clearTimeout(timer);
    };
  }, []);

  if (isStandalone()) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDeferredEvent(null);
    setShowIOSSheet(false);
  };

  const install = async () => {
    if (!deferredEvent) return;
    await deferredEvent.prompt();
    await deferredEvent.userChoice.catch(() => {});
    dismiss();
  };

  // Android / Chrome banner
  if (deferredEvent) {
    return (
      <div
        className="fixed left-4 right-4 bottom-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-50 ts-card p-5 animate-fade-in shadow-2xl"
        data-testid="pwa-install-banner"
        style={{ background: "#1A1A1A", borderColor: "#C9A84C40" }}
      >
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute top-3 right-3 text-[#9A9590] hover:text-[#F0EDE8]"
          data-testid="pwa-install-dismiss"
        >
          <X size={16} />
        </button>
        <div className="overline">Install the table</div>
        <h3 className="font-serif text-lg text-[#F0EDE8] mt-2 leading-snug">
          Keep The Synergy Table one tap away.
        </h3>
        <p className="text-sm text-[#9A9590] mt-2 leading-relaxed">
          Add it to your home screen — fullscreen, no browser bar, faster load.
        </p>
        <button
          onClick={install}
          className="btn-gold rounded-md px-4 py-2.5 mt-4 inline-flex items-center gap-2 text-sm"
          data-testid="pwa-install-confirm"
        >
          <Download size={14} /> Install
        </button>
      </div>
    );
  }

  // iOS Safari sheet
  if (showIOSSheet) {
    return (
      <div
        className="fixed inset-x-0 bottom-0 z-50 px-4 pb-[max(env(safe-area-inset-bottom),16px)] animate-fade-in"
        data-testid="pwa-install-ios"
      >
        <div
          className="ts-card p-5 max-w-md mx-auto shadow-2xl relative"
          style={{ background: "#1A1A1A", borderColor: "#C9A84C55" }}
        >
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            className="absolute top-3 right-3 text-[#9A9590] hover:text-[#F0EDE8]"
            data-testid="pwa-install-ios-dismiss"
          >
            <X size={16} />
          </button>
          <div className="overline">Install on iPhone</div>
          <h3 className="font-serif text-lg text-[#F0EDE8] mt-2 leading-snug">
            Add Synergy to your Home Screen
          </h3>
          <ol className="text-sm text-[#F0EDE8] mt-3 space-y-2 leading-relaxed">
            <li className="flex items-start gap-3">
              <span className="text-[#C9A84C] font-serif text-base shrink-0 w-5">1.</span>
              <span>
                Tap the <Share size={13} className="inline -mt-0.5 text-[#C9A84C]" />{" "}
                <span className="text-[#C9A84C]">Share</span> button at the bottom of Safari.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#C9A84C] font-serif text-base shrink-0 w-5">2.</span>
              <span>
                Scroll and tap{" "}
                <span className="inline-flex items-center gap-1 text-[#C9A84C]">
                  <Plus size={13} /> Add to Home Screen
                </span>
                .
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#C9A84C] font-serif text-base shrink-0 w-5">3.</span>
              <span>Tap <span className="text-[#C9A84C]">Add</span> — that's it.</span>
            </li>
          </ol>
          <button
            onClick={dismiss}
            className="text-xs uppercase tracking-[0.25em] text-[#9A9590] hover:text-[#C9A84C] mt-5"
          >
            Maybe later
          </button>
        </div>
      </div>
    );
  }

  return null;
}
